import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Badge } from './badge';

interface DebouncedTextInputProps {
  label: string;
  /** Authoritative value from the server; seeds the local draft on mount. */
  value: string;
  /** Render a multi-line textarea instead of an input. */
  multiline?: boolean;
  /**
   * Called `wait`ms after the user stops typing. Empty strings are
   * normalised to `undefined` so callers can map this directly onto a
   * PATCH body without nulling the field accidentally.
   */
  onCommit: (next: string | undefined) => void;
  /** Idle milliseconds before firing the commit. Defaults to 800. */
  wait?: number;
}

type SaveState = 'idle' | 'dirty' | 'saved';

/**
 * Text field that holds a local draft and pushes to `onCommit` after
 * `wait`ms of idle. Renders a small status pill beside the label:
 * "Draft" while the draft differs from the last-committed value,
 * "Saved" for a couple of seconds after a commit, nothing otherwise.
 * Keeps PATCH-on-every-keystroke churn off upstream queries.
 */
export const DebouncedTextInput = ({
  label,
  value,
  multiline = false,
  onCommit,
  wait = 800,
}: DebouncedTextInputProps) => {
  const [draft, setDraft] = useState(value);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Pin the debounced fn via ref so the unmount-cleanup effect can cancel
  // without listing the unstable `commit` reference in its deps (that
  // would re-run the cleanup on every render instead of only on unmount).
  const commitRef = useRef<ReturnType<typeof useDebouncedCallback> | null>(null);

  const commit = useDebouncedCallback((next: string) => {
    onCommit(next.trim() || undefined);
    setSaveState('saved');
    if (savedTimerRef.current) {
      clearTimeout(savedTimerRef.current);
    }
    savedTimerRef.current = setTimeout(() => setSaveState('idle'), 2000);
  }, wait);
  commitRef.current = commit;

  useEffect(() => () => {
    if (savedTimerRef.current) {
      clearTimeout(savedTimerRef.current);
    }
    commitRef.current?.cancel();
  }, []);

  const handleChange = (next: string) => {
    setDraft(next);
    setSaveState('dirty');
    commit(next);
  };

  const statusPill: Record<SaveState, ReactNode> = {
    idle: null,
    dirty: (
      <Badge variant='outline' className='tw:text-ThemeTextLight tw:font-normal'>
        Draft
      </Badge>
    ),
    saved: (
      <Badge variant='outline' className='tw:text-emerald-700 tw:border-emerald-600/30 tw:font-normal'>
        Saved
      </Badge>
    ),
  };

  return (
    <label className='tw:flex tw:flex-col tw:gap-1 tw:text-xs'>
      <span className='tw:flex tw:items-center tw:gap-2'>
        <span>{label}</span>
        {statusPill[saveState]}
      </span>
      {multiline ? (
        <textarea
          rows={2}
          className='tw:rounded tw:border tw:border-border tw:p-1 tw:text-sm'
          value={draft}
          onChange={(e) => handleChange(e.target.value)}
        />
      ) : (
        <input
          type='text'
          className='tw:rounded tw:border tw:border-border tw:p-1 tw:text-sm'
          value={draft}
          onChange={(e) => handleChange(e.target.value)}
        />
      )}
    </label>
  );
};
